import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  useQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import {
  Plus,
  Trash2,
  ShoppingCart,
  Check,
  ChevronLeft,
  X,
  Loader2,
  ListChecks,
  WifiOff,
} from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Modal } from '../components/ui/Modal';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { toast } from '../components/ui/Toast';
import { shoppingListsApi } from '../api/shoppingLists';
import { householdsApi } from '../api/households';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import {
  cacheShoppingLists,
  getCachedShoppingLists,
  toggleCachedItem,
} from '../lib/offlineDb';
import { syncShoppingListChanges } from '../lib/syncManager';
import type {
  ShoppingList,
  ShoppingListItem,
  ShoppingListSuggestion,
  Household,
} from '../types';

// =============================================
// Offline Banner
// =============================================
const OfflineBanner: React.FC = () => (
  <div
    role="status"
    aria-live="polite"
    className="flex items-center gap-2 px-4 py-3 rounded-xl
               bg-amber-50 dark:bg-amber-500/10
               border border-amber-200 dark:border-amber-500/20
               text-amber-700 dark:text-amber-400
               text-sm font-medium"
  >
    <WifiOff className="w-4 h-4 shrink-0" />
    <span>Offline — Änderungen werden synchronisiert sobald du online bist</span>
  </div>
);

// =============================================
// Progress Bar
// =============================================
const ProgressBar: React.FC<{ checked: number; total: number }> = ({ checked, total }) => {
  const pct = total > 0 ? Math.round((checked / total) * 100) : 0;
  return (
    <div className="w-full h-2 bg-neutral-100 dark:bg-neutral-800 rounded-full overflow-hidden">
      <div
        className="h-full rounded-full bg-gradient-to-r from-primary-500 to-secondary-500 transition-all duration-500"
        style={{ width: `${pct}%` }}
      />
    </div>
  );
};

// =============================================
// Empty State — Keine Liste vorhanden
// =============================================
const EmptyState: React.FC<{ onCreate: () => void }> = ({ onCreate }) => (
  <div className="flex flex-col items-center justify-center py-16 px-4 text-center animate-fade-in">
    <div className="w-20 h-20 mb-5 gradient-primary rounded-3xl flex items-center justify-center shadow-glow-primary">
      <ShoppingCart className="w-10 h-10 text-white" />
    </div>
    <h2 className="text-xl font-bold text-neutral-800 dark:text-white mb-2">
      Noch keine Einkaufsliste
    </h2>
    <p className="text-neutral-500 dark:text-neutral-400 text-sm mb-8 max-w-xs">
      Erstelle deine erste Einkaufsliste und behalte den Überblick beim Einkaufen.
    </p>
    <button
      onClick={onCreate}
      aria-label="Neue Einkaufsliste erstellen"
      className="w-20 h-20 rounded-full gradient-primary shadow-glow-primary
                 flex items-center justify-center
                 hover:scale-105 active:scale-95 transition-transform duration-200"
    >
      <Plus className="w-10 h-10 text-white" />
    </button>
    <p className="mt-4 text-sm text-neutral-400 dark:text-neutral-500 font-medium">
      Neue Einkaufsliste erstellen
    </p>
  </div>
);

// =============================================
// List Overview Card
// =============================================
const ListOverviewCard: React.FC<{
  list: ShoppingList;
  onClick: () => void;
  onDelete: () => void;
  isOffline: boolean;
}> = ({ list, onClick, onDelete, isOffline }) => (
  <Card hover onClick={onClick} className="group">
    <div className="flex items-center gap-4">
      <div className="w-12 h-12 bg-primary-50 dark:bg-primary-500/10 text-primary-500 rounded-xl flex items-center justify-center shrink-0
                      group-hover:bg-primary-100 dark:group-hover:bg-primary-500/20 transition-colors">
        <ListChecks className="w-6 h-6" />
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="text-sm font-semibold text-neutral-800 dark:text-neutral-100 truncate">
          {list.name}
        </h3>
        <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-0.5">
          {list.checked_count} von {list.item_count} erledigt
        </p>
        <div className="mt-2">
          <ProgressBar checked={list.checked_count} total={list.item_count} />
        </div>
      </div>
      {!isOffline && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          aria-label={`Liste "${list.name}" löschen`}
          className="p-2 rounded-lg text-neutral-300 hover:text-error-500 hover:bg-error-50
                     dark:hover:bg-error-500/10 transition-colors shrink-0 opacity-0 group-hover:opacity-100"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      )}
    </div>
  </Card>
);

// =============================================
// Item Row
// =============================================
const ItemRow: React.FC<{
  item: ShoppingListItem;
  onToggle: () => void;
  onDelete: () => void;
  isToggling: boolean;
  isOffline: boolean;
}> = ({ item, onToggle, onDelete, isToggling, isOffline }) => (
  <div
    className={`flex items-center gap-3 px-1 py-2 rounded-xl transition-all duration-200
                ${item.is_checked ? 'opacity-50' : ''}`}
  >
    {/* Checkbox */}
    <button
      onClick={onToggle}
      disabled={isToggling}
      aria-label={item.is_checked ? `${item.name} als nicht erledigt markieren` : `${item.name} als erledigt markieren`}
      aria-checked={item.is_checked}
      role="checkbox"
      className={`
        w-11 h-11 rounded-xl border-2 flex items-center justify-center shrink-0
        transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500/30
        ${item.is_checked
          ? 'bg-gradient-to-br from-primary-500 to-secondary-500 border-transparent shadow-md'
          : 'border-neutral-300 dark:border-neutral-600 hover:border-primary-400 dark:hover:border-primary-500 bg-white dark:bg-transparent'
        }
        ${isToggling ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer active:scale-90'}
      `}
    >
      {isToggling ? (
        <Loader2 className="w-4 h-4 text-primary-500 animate-spin" />
      ) : item.is_checked ? (
        <Check className="w-5 h-5 text-white" strokeWidth={3} />
      ) : null}
    </button>

    {/* Name + Menge */}
    <div className="flex-1 min-w-0">
      <p className={`text-sm font-medium text-neutral-800 dark:text-neutral-100 truncate transition-all duration-300
                     ${item.is_checked ? 'line-through text-neutral-400 dark:text-neutral-500' : ''}`}>
        {item.name}
      </p>
      {item.quantity && (
        <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-0.5">{item.quantity}</p>
      )}
    </div>

    {/* Delete Button — nur online verfuegbar */}
    {!isOffline && (
      <button
        onClick={onDelete}
        aria-label={`${item.name} entfernen`}
        className="w-8 h-8 rounded-lg text-neutral-300 dark:text-neutral-600
                   hover:text-error-500 hover:bg-error-50 dark:hover:bg-error-500/10
                   flex items-center justify-center shrink-0 transition-colors"
      >
        <X className="w-4 h-4" />
      </button>
    )}
  </div>
);

// =============================================
// Add Item Input with Suggestions
// =============================================
interface AddItemInputProps {
  listId: string;
  onAdded: () => void;
}

const AddItemInput: React.FC<AddItemInputProps> = ({ listId, onAdded }) => {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [quantity, setQuantity] = useState('');
  const [suggestions, setSuggestions] = useState<ShoppingListSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIdx, setHighlightedIdx] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const addMutation = useMutation({
    mutationFn: (data: { name: string; quantity?: string }) =>
      shoppingListsApi.addItem(listId, {
        name: data.name.trim(),
        quantity: data.quantity?.trim() || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shopping-list', listId] });
      queryClient.invalidateQueries({ queryKey: ['shopping-lists'] });
      setName('');
      setQuantity('');
      setSuggestions([]);
      setShowSuggestions(false);
      onAdded();
      inputRef.current?.focus();
    },
    onError: () => {
      toast.error('Fehler', 'Artikel konnte nicht hinzugefügt werden.');
    },
  });

  const fetchSuggestions = useCallback(async (q: string) => {
    if (!q.trim()) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    try {
      const data = await shoppingListsApi.suggestions(q.trim());
      setSuggestions(data.slice(0, 6));
      setShowSuggestions(data.length > 0);
    } catch {
      setSuggestions([]);
    }
  }, []);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setName(val);
    setHighlightedIdx(-1);
    if (suggestTimeout.current) clearTimeout(suggestTimeout.current);
    suggestTimeout.current = setTimeout(() => fetchSuggestions(val), 220);
  };

  const handleSubmit = () => {
    if (!name.trim()) return;
    addMutation.mutate({ name, quantity });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightedIdx >= 0 && suggestions[highlightedIdx]) {
        selectSuggestion(suggestions[highlightedIdx]);
      } else {
        handleSubmit();
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIdx((prev) => Math.min(prev + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIdx((prev) => Math.max(prev - 1, -1));
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  const selectSuggestion = (s: ShoppingListSuggestion) => {
    setName(s.name);
    setSuggestions([]);
    setShowSuggestions(false);
    inputRef.current?.focus();
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (suggestTimeout.current) clearTimeout(suggestTimeout.current);
    };
  }, []);

  return (
    <div className="relative">
      <div className="flex gap-2">
        {/* Name Input */}
        <div className="relative flex-1">
          <input
            ref={inputRef}
            type="text"
            value={name}
            onChange={handleNameChange}
            onKeyDown={handleKeyDown}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
            onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
            placeholder="Artikel hinzufügen..."
            aria-label="Artikelname"
            autoComplete="off"
            className="w-full px-4 py-3 text-sm border border-neutral-200 dark:border-indigo-500/20 rounded-xl
                       bg-white/70 dark:bg-[#1a1a2e]/80 backdrop-blur-sm dark:text-white
                       placeholder:text-neutral-400 dark:placeholder:text-neutral-500
                       focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-400
                       dark:focus:border-indigo-500/40 transition-all duration-200"
          />

          {/* Suggestion Dropdown */}
          {showSuggestions && suggestions.length > 0 && (
            <div
              className="absolute left-0 right-0 top-full mt-1 z-20
                         bg-white dark:bg-[#1a1a2e] border border-neutral-200 dark:border-indigo-500/20
                         rounded-xl shadow-lg overflow-hidden"
              role="listbox"
              aria-label="Vorschläge"
            >
              {suggestions.map((s, idx) => (
                <button
                  key={s.name}
                  role="option"
                  aria-selected={idx === highlightedIdx}
                  onMouseDown={() => selectSuggestion(s)}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left transition-colors
                               ${idx === highlightedIdx
                                 ? 'bg-primary-50 dark:bg-primary-500/10 text-primary-700 dark:text-primary-300'
                                 : 'text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-white/5'
                               }`}
                >
                  <span className="flex-1 truncate font-medium">{s.name}</span>
                  {s.category_name && (
                    <span className="text-xs text-neutral-400 dark:text-neutral-500 shrink-0">
                      {s.category_name}
                    </span>
                  )}
                  <span className="text-xs text-neutral-300 dark:text-neutral-600 shrink-0">
                    {s.count}x
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Quantity Input — compact */}
        <input
          type="text"
          value={quantity}
          onChange={(e) => setQuantity(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
          placeholder="Menge"
          aria-label="Menge (optional)"
          className="w-20 px-3 py-3 text-sm border border-neutral-200 dark:border-indigo-500/20 rounded-xl
                     bg-white/70 dark:bg-[#1a1a2e]/80 backdrop-blur-sm dark:text-white
                     placeholder:text-neutral-400 dark:placeholder:text-neutral-500
                     focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-400
                     dark:focus:border-indigo-500/40 transition-all duration-200 text-center"
        />

        {/* Add Button */}
        <button
          onClick={handleSubmit}
          disabled={!name.trim() || addMutation.isPending}
          aria-label="Artikel hinzufügen"
          className="w-12 h-12 rounded-xl gradient-primary shadow-glow-primary text-white
                     flex items-center justify-center shrink-0
                     hover:scale-105 active:scale-95 transition-transform duration-200
                     disabled:opacity-50 disabled:hover:scale-100 disabled:cursor-not-allowed"
        >
          {addMutation.isPending ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <Plus className="w-5 h-5" />
          )}
        </button>
      </div>
    </div>
  );
};

// =============================================
// Create List Modal
// =============================================
interface CreateListModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (list: ShoppingList) => void;
}

const CreateListModal: React.FC<CreateListModalProps> = ({ isOpen, onClose, onCreated }) => {
  const [listName, setListName] = useState('Einkaufsliste');
  const [useHousehold, setUseHousehold] = useState(false);
  const queryClient = useQueryClient();

  const { data: households } = useQuery<Household[]>({
    queryKey: ['households'],
    queryFn: householdsApi.list,
  });
  const household = households && households.length > 0 ? households[0] : null;

  const createMutation = useMutation({
    mutationFn: () =>
      shoppingListsApi.create({
        name: listName.trim() || 'Einkaufsliste',
        household_id: useHousehold && household ? household.id : undefined,
      }),
    onSuccess: (newList) => {
      queryClient.invalidateQueries({ queryKey: ['shopping-lists'] });
      toast.success('Einkaufsliste erstellt');
      onCreated(newList);
      onClose();
      setListName('Einkaufsliste');
      setUseHousehold(false);
    },
    onError: () => {
      toast.error('Fehler', 'Liste konnte nicht erstellt werden.');
    },
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Neue Einkaufsliste" size="sm">
      <div className="space-y-5">
        <div>
          <label
            htmlFor="list-name"
            className="block text-sm font-semibold text-neutral-600 dark:text-neutral-300 mb-1.5"
          >
            Name
          </label>
          <input
            id="list-name"
            type="text"
            value={listName}
            onChange={(e) => setListName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && createMutation.mutate()}
            autoFocus
            className="w-full px-4 py-3 text-sm border border-neutral-200 dark:border-indigo-500/20 rounded-xl
                       bg-white/70 dark:bg-[#1a1a2e]/80 backdrop-blur-sm dark:text-white
                       placeholder:text-neutral-400 focus:outline-none focus:ring-2
                       focus:ring-primary-500/20 focus:border-primary-400 transition-all duration-200"
          />
        </div>

        {household && (
          <div>
            <label className="flex items-center gap-3 cursor-pointer group">
              <div
                onClick={() => setUseHousehold(!useHousehold)}
                className={`relative w-11 h-6 rounded-full transition-colors duration-200
                             ${useHousehold
                               ? 'bg-gradient-to-r from-primary-500 to-secondary-500'
                               : 'bg-neutral-200 dark:bg-neutral-700'
                             }`}
              >
                <div
                  className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow-md transition-transform duration-200
                               ${useHousehold ? 'translate-x-5' : 'translate-x-0.5'}`}
                />
              </div>
              <div>
                <p className="text-sm font-medium text-neutral-700 dark:text-neutral-200">
                  Mit Haushalt teilen
                </p>
                <p className="text-xs text-neutral-400 dark:text-neutral-500">
                  {household.name}
                </p>
              </div>
            </label>
          </div>
        )}

        <div className="flex gap-3 pt-1">
          <Button variant="outline" fullWidth onClick={onClose}>
            Abbrechen
          </Button>
          <Button
            fullWidth
            loading={createMutation.isPending}
            onClick={() => createMutation.mutate()}
            disabled={!listName.trim()}
          >
            Erstellen
          </Button>
        </div>
      </div>
    </Modal>
  );
};

// =============================================
// Detail View — Hauptansicht
// =============================================
interface DetailViewProps {
  listId: string;
  onBack: () => void;
  isOffline: boolean;
}

const DetailView: React.FC<DetailViewProps> = ({ listId, onBack, isOffline }) => {
  const queryClient = useQueryClient();
  const [togglingIds, setTogglingIds] = useState<Set<string>>(new Set());
  // Offline-lokale Listendaten (aus IndexedDB)
  const [offlineList, setOfflineList] = useState<ShoppingList | null>(null);

  // Online: Daten vom Server laden und im Hintergrund cachen
  const { data: onlineList, isLoading, isError } = useQuery<ShoppingList>({
    queryKey: ['shopping-list', listId],
    queryFn: async () => {
      const result = await shoppingListsApi.get(listId);
      // Im Hintergrund cachen (einzelne Liste als Array uebergeben)
      cacheShoppingLists([result]).catch(() => {/* silent */});
      return result;
    },
    refetchInterval: isOffline ? false : 30_000,
    enabled: !isOffline,
  });

  // Offline: Daten aus IndexedDB laden
  useEffect(() => {
    if (isOffline) {
      getCachedShoppingLists()
        .then((cached) => {
          const found = cached.find((l) => l.id === listId) ?? null;
          setOfflineList(found);
        })
        .catch(() => setOfflineList(null));
    }
  }, [isOffline, listId]);

  const list = isOffline ? offlineList : onlineList;

  // Optimistisches Toggle — funktioniert online und offline
  const handleToggle = useCallback(
    async (itemId: string, is_checked: boolean) => {
      setTogglingIds((prev) => new Set(prev).add(itemId));

      if (isOffline) {
        // Offline: nur IndexedDB updaten, optimistisch im State reflektieren
        try {
          await toggleCachedItem(itemId, is_checked);
          setOfflineList((prev) => {
            if (!prev) return prev;
            const updatedItems = prev.items.map((i) =>
              i.id === itemId ? { ...i, is_checked } : i
            );
            return {
              ...prev,
              items: updatedItems,
              checked_count: updatedItems.filter((i) => i.is_checked).length,
            };
          });
        } catch {
          toast.error('Fehler', 'Änderung konnte nicht gespeichert werden.');
        } finally {
          setTogglingIds((prev) => {
            const next = new Set(prev);
            next.delete(itemId);
            return next;
          });
        }
      } else {
        // Online: optimistisches Update + API-Call via React Query
        toggleOnlineMutation.mutate({ itemId, is_checked });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isOffline, listId]
  );

  const toggleOnlineMutation = useMutation({
    mutationFn: ({ itemId, is_checked }: { itemId: string; is_checked: boolean }) =>
      shoppingListsApi.updateItem(listId, itemId, { is_checked }),

    onMutate: async ({ itemId, is_checked }) => {
      setTogglingIds((prev) => new Set(prev).add(itemId));
      await queryClient.cancelQueries({ queryKey: ['shopping-list', listId] });
      const previous = queryClient.getQueryData<ShoppingList>(['shopping-list', listId]);

      queryClient.setQueryData<ShoppingList>(['shopping-list', listId], (old) => {
        if (!old) return old;
        return {
          ...old,
          items: old.items.map((item) =>
            item.id === itemId ? { ...item, is_checked } : item,
          ),
          checked_count: old.items.reduce(
            (acc, item) =>
              item.id === itemId
                ? acc + (is_checked ? 1 : -1)
                : acc + (item.is_checked ? 1 : 0),
            0,
          ),
        };
      });

      return { previous };
    },

    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) {
        queryClient.setQueryData(['shopping-list', listId], ctx.previous);
      }
      toast.error('Fehler', 'Artikel konnte nicht aktualisiert werden.');
    },

    onSettled: (_data, _err, { itemId }) => {
      setTogglingIds((prev) => {
        const next = new Set(prev);
        next.delete(itemId);
        return next;
      });
      queryClient.invalidateQueries({ queryKey: ['shopping-list', listId] });
      queryClient.invalidateQueries({ queryKey: ['shopping-lists'] });
    },
  });

  const deleteItemMutation = useMutation({
    mutationFn: (itemId: string) => shoppingListsApi.deleteItem(listId, itemId),
    onMutate: async (itemId) => {
      await queryClient.cancelQueries({ queryKey: ['shopping-list', listId] });
      const previous = queryClient.getQueryData<ShoppingList>(['shopping-list', listId]);
      queryClient.setQueryData<ShoppingList>(['shopping-list', listId], (old) => {
        if (!old) return old;
        const filtered = old.items.filter((i) => i.id !== itemId);
        return {
          ...old,
          items: filtered,
          item_count: filtered.length,
          checked_count: filtered.filter((i) => i.is_checked).length,
        };
      });
      return { previous };
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.previous) {
        queryClient.setQueryData(['shopping-list', listId], ctx.previous);
      }
      toast.error('Fehler', 'Artikel konnte nicht entfernt werden.');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['shopping-list', listId] });
      queryClient.invalidateQueries({ queryKey: ['shopping-lists'] });
    },
  });

  const clearCheckedMutation = useMutation({
    mutationFn: () => shoppingListsApi.clearChecked(listId),
    onSuccess: (updatedList) => {
      queryClient.setQueryData(['shopping-list', listId], updatedList);
      queryClient.invalidateQueries({ queryKey: ['shopping-lists'] });
      toast.success('Erledigte Artikel entfernt');
    },
    onError: () => {
      toast.error('Fehler', 'Erledigte Artikel konnten nicht entfernt werden.');
    },
  });

  if (!isOffline && isLoading) {
    return <LoadingSpinner fullPage text="Liste wird geladen..." />;
  }

  if (!isOffline && (isError || !list)) {
    return (
      <Card>
        <p className="text-center py-8 text-error-500">
          Liste konnte nicht geladen werden.
        </p>
      </Card>
    );
  }

  // Offline und noch kein Cache verfuegbar
  if (isOffline && !offlineList) {
    return (
      <div className="space-y-4 animate-fade-in">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            aria-label="Zurück zur Übersicht"
            className="p-2 rounded-xl text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200
                       hover:bg-white/60 dark:hover:bg-white/5 transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
        </div>
        <OfflineBanner />
        <Card>
          <p className="text-center py-8 text-neutral-500 dark:text-neutral-400">
            Diese Liste ist noch nicht gecacht. Öffne sie einmal online, damit sie offline verfügbar ist.
          </p>
        </Card>
      </div>
    );
  }

  if (!list) return null;

  const uncheckedItems = list.items.filter((i) => !i.is_checked);
  const checkedItems = list.items.filter((i) => i.is_checked);
  const hasChecked = checkedItems.length > 0;

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Offline Banner */}
      {isOffline && <OfflineBanner />}

      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={onBack}
          aria-label="Zurück zur Übersicht"
          className="p-2 rounded-xl text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200
                     hover:bg-white/60 dark:hover:bg-white/5 transition-colors"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-bold text-neutral-800 dark:text-white truncate">
            {list.name}
          </h1>
          <p className="text-sm text-neutral-400 dark:text-neutral-500 mt-0.5">
            {list.checked_count} von {list.item_count} erledigt
          </p>
        </div>
      </div>

      {/* Progress */}
      {list.item_count > 0 && (
        <ProgressBar checked={list.checked_count} total={list.item_count} />
      )}

      {/* Add Item — nur online */}
      {!isOffline && (
        <Card padding="sm">
          <AddItemInput
            listId={listId}
            onAdded={() => {/* focus is handled inside */}}
          />
        </Card>
      )}

      {/* Item List */}
      {list.item_count === 0 ? (
        <div className="py-12 text-center animate-bounce-in">
          <span className="text-5xl block mb-3">🛒</span>
          <p className="text-neutral-500 dark:text-neutral-400 font-medium">
            Noch keine Artikel
          </p>
          <p className="text-neutral-400 dark:text-neutral-500 text-sm mt-1">
            Füge deinen ersten Artikel oben ein.
          </p>
        </div>
      ) : (
        <div className="space-y-1">
          {/* Unchecked Items */}
          {uncheckedItems.map((item) => (
            <ItemRow
              key={item.id}
              item={item}
              isToggling={togglingIds.has(item.id)}
              isOffline={isOffline}
              onToggle={() => handleToggle(item.id, true)}
              onDelete={() => deleteItemMutation.mutate(item.id)}
            />
          ))}

          {/* Divider */}
          {uncheckedItems.length > 0 && checkedItems.length > 0 && (
            <div className="flex items-center gap-3 py-2">
              <div className="flex-1 h-px bg-neutral-100 dark:bg-neutral-800" />
              <span className="text-xs text-neutral-400 dark:text-neutral-500 font-medium">
                Erledigt ({checkedItems.length})
              </span>
              <div className="flex-1 h-px bg-neutral-100 dark:bg-neutral-800" />
            </div>
          )}

          {/* Checked Items */}
          {checkedItems.map((item) => (
            <ItemRow
              key={item.id}
              item={item}
              isToggling={togglingIds.has(item.id)}
              isOffline={isOffline}
              onToggle={() => handleToggle(item.id, false)}
              onDelete={() => deleteItemMutation.mutate(item.id)}
            />
          ))}
        </div>
      )}

      {/* Bottom Actions — nur online */}
      {!isOffline && hasChecked && (
        <div className="pt-2">
          <Button
            variant="outline"
            fullWidth
            loading={clearCheckedMutation.isPending}
            onClick={() => clearCheckedMutation.mutate()}
            className="text-error-500 !border-error-200 dark:!border-error-500/20 hover:!bg-error-50 dark:hover:!bg-error-500/10"
          >
            <Trash2 className="w-4 h-4" />
            Erledigte entfernen ({checkedItems.length})
          </Button>
        </div>
      )}
    </div>
  );
};

// =============================================
// Shopping List Page — Root
// =============================================
export const ShoppingListPage: React.FC = () => {
  const queryClient = useQueryClient();
  const isOnline = useOnlineStatus();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [activeListId, setActiveListId] = useState<string | null>(null);
  // Offline-Listen aus IndexedDB
  const [offlineLists, setOfflineLists] = useState<ShoppingList[]>([]);

  // Online: Listen laden und cachen
  const { data: onlineLists = [], isLoading, isError } = useQuery<ShoppingList[]>({
    queryKey: ['shopping-lists'],
    queryFn: async () => {
      const result = await shoppingListsApi.list();
      // Im Hintergrund alle Listen (mit Items) cachen
      // Dafuer jede Liste einzeln laden, damit Items gecacht werden
      Promise.all(
        result.map(async (l) => {
          try {
            const full = await shoppingListsApi.get(l.id);
            return full;
          } catch {
            return l;
          }
        })
      )
        .then((full) => cacheShoppingLists(full))
        .catch(() => {/* silent */});
      return result;
    },
    enabled: isOnline,
  });

  // Offline: gecachte Listen laden
  useEffect(() => {
    if (!isOnline) {
      getCachedShoppingLists()
        .then(setOfflineLists)
        .catch(() => setOfflineLists([]));
    }
  }, [isOnline]);

  // Bei Netzwerk-Rueckkehr: Aenderungen syncen und Daten neu laden
  const prevOnline = useRef(isOnline);
  useEffect(() => {
    if (!prevOnline.current && isOnline) {
      // Gerade online gegangen
      syncShoppingListChanges()
        .then(({ successCount }) => {
          if (successCount > 0) {
            toast.success(`${successCount} Änderung${successCount === 1 ? '' : 'en'} synchronisiert`);
          }
        })
        .catch(() => {/* silent */});
      queryClient.invalidateQueries({ queryKey: ['shopping-lists'] });
      if (activeListId) {
        queryClient.invalidateQueries({ queryKey: ['shopping-list', activeListId] });
      }
    }
    prevOnline.current = isOnline;
  }, [isOnline, queryClient, activeListId]);

  const lists = isOnline ? onlineLists : offlineLists;

  const deleteListMutation = useMutation({
    mutationFn: (id: string) => shoppingListsApi.delete(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: ['shopping-lists'] });
      if (activeListId === id) setActiveListId(null);
      toast.success('Liste gelöscht');
    },
    onError: () => {
      toast.error('Fehler', 'Liste konnte nicht gelöscht werden.');
    },
  });

  // Auto-open the only list if there is exactly one
  useEffect(() => {
    if (lists.length === 1 && !activeListId) {
      setActiveListId(lists[0].id);
    }
  }, [lists, activeListId]);

  const handleCreated = (list: ShoppingList) => {
    setActiveListId(list.id);
  };

  // Detail view
  if (activeListId) {
    return (
      <>
        <DetailView
          listId={activeListId}
          isOffline={!isOnline}
          onBack={() => {
            setActiveListId(null);
            queryClient.invalidateQueries({ queryKey: ['shopping-lists'] });
          }}
        />
        <CreateListModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onCreated={handleCreated}
        />
      </>
    );
  }

  // Loading (nur online)
  if (isOnline && isLoading) {
    return <LoadingSpinner fullPage text="Listen werden geladen..." />;
  }

  // Error (nur online)
  if (isOnline && isError) {
    return (
      <Card>
        <p className="text-center py-8 text-error-500">
          Daten konnten nicht geladen werden. Bitte versuche es erneut.
        </p>
      </Card>
    );
  }

  // No lists
  if (lists.length === 0) {
    return (
      <>
        {!isOnline && (
          <div className="mb-4">
            <OfflineBanner />
          </div>
        )}
        {isOnline ? (
          <>
            <EmptyState onCreate={() => setShowCreateModal(true)} />
            <CreateListModal
              isOpen={showCreateModal}
              onClose={() => setShowCreateModal(false)}
              onCreated={handleCreated}
            />
          </>
        ) : (
          <Card>
            <p className="text-center py-8 text-neutral-500 dark:text-neutral-400">
              Keine gecachten Listen verfügbar. Öffne die App einmal online.
            </p>
          </Card>
        )}
      </>
    );
  }

  // List overview (multiple lists)
  return (
    <div className="space-y-6 animate-fade-in">
      {/* Offline Banner */}
      {!isOnline && <OfflineBanner />}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-neutral-800 dark:text-white">
            Einkaufslisten
          </h1>
          <p className="text-neutral-500 dark:text-neutral-400 mt-1">
            {lists.length} {lists.length === 1 ? 'Liste' : 'Listen'}
          </p>
        </div>
        {isOnline && (
          <Button onClick={() => setShowCreateModal(true)} icon={<Plus className="w-4 h-4" />}>
            Neue Liste
          </Button>
        )}
      </div>

      {/* Cards */}
      <div className="space-y-3">
        {lists.map((list) => (
          <ListOverviewCard
            key={list.id}
            list={list}
            isOffline={!isOnline}
            onClick={() => setActiveListId(list.id)}
            onDelete={() => deleteListMutation.mutate(list.id)}
          />
        ))}
      </div>

      {isOnline && (
        <CreateListModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onCreated={handleCreated}
        />
      )}
    </div>
  );
};
